import React, { useCallback, useMemo } from "react";
import {
  decideTPRMApprovalApi,
  getTPRMApprovalsApi,
  verifyTPRMApprovalConditionApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import TPRMResourceTable, { StatusBadge } from "./TPRMResourceTable";
import {
  displayTPRMValue,
  formatTPRMDate,
} from "./tprmPageUtils";

const DECISIONS = [
  "Approved",
  "Conditionally Approved",
  "Rejected",
  "Deferred",
  "Changes Requested",
];

const FILTERS = [
  {
    id: "decision",
    definition: {
      variant: "select",
      label: "Decision status",
      options: ["Pending", ...DECISIONS, "Expired"].map((value) => ({
        value,
        label: value,
      })),
    },
  },
];

const ApprovalDecisionDialog = {
  title: "Decide approval",
  description:
    "A justification is required. Conditional approval requires one condition per line.",
  fields: [
    {
      name: "decision",
      label: "Decision",
      type: "select",
      required: true,
      defaultValue: "Approved",
      options: DECISIONS.map((value) => ({ value, label: value })),
    },
    {
      name: "justification",
      label: "Justification",
      type: "textarea",
      required: true,
    },
    {
      name: "conditions",
      label: "Conditions (required for conditional approval)",
      type: "textarea",
    },
  ],
};

const ApprovalsPage = () => {
  const fetchPage = useCallback((params) => getTPRMApprovalsApi(params), []);
  const columns = useMemo(
    () => [
      {
        accessorKey: "approval_id",
        header: "Approval ID",
        size: 165,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "service",
        id: "service",
        accessorFn: (approval) => approval.service,
        header: "Related service reference",
        size: 220,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "requested_by",
        header: "Requested by",
        size: 155,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "requested_at",
        header: "Request date",
        size: 140,
        cell: ({ getValue }) => formatTPRMDate(getValue()),
      },
      {
        accessorKey: "decision",
        header: "Decision status",
        size: 180,
        cell: ({ getValue }) => <StatusBadge value={getValue()} />,
      },
      {
        accessorKey: "decided_at",
        header: "Decision date",
        size: 140,
        cell: ({ getValue }) => formatTPRMDate(getValue()),
      },
    ],
    [],
  );

  const getActions = useCallback((approval, { canApprove }) => {
    if (!canApprove) return [];
    const available = [];
    if (approval.decision === "Pending") {
      available.push({
        action: "decide",
        label: "Decide",
        dialog: ApprovalDecisionDialog,
        initialValues: { decision: "Approved" },
      });
    }
    if (approval.decision === "Conditionally Approved") {
      (approval.conditions || []).forEach((condition, index) => {
        if (!condition?.verified) {
          available.push({
            action: "verify_condition",
            label: `Verify condition ${index + 1}`,
            payload: { condition_index: index },
          });
        }
      });
    }
    return available;
  }, []);

  const performAction = useCallback((approval, item, values) => {
    if (item.action === "verify_condition") {
      return verifyTPRMApprovalConditionApi(
        approval.approval_id,
        item.payload.condition_index,
      );
    }
    if (item.action === "decide") {
      const conditions = String(values.conditions || "")
        .split(/\r?\n/)
        .map((description) => description.trim())
        .filter(Boolean)
        .map((description) => ({ description }));
      if (values.decision === "Conditionally Approved" && !conditions.length) {
        throw new Error("Enter at least one condition for conditional approval.");
      }
      return decideTPRMApprovalApi(approval.approval_id, {
        decision: values.decision,
        justification: values.justification,
        ...(values.decision === "Conditionally Approved" ? { conditions } : {}),
      });
    }
    throw new Error("Unsupported approval action.");
  }, []);

  return (
    <TPRMResourceTable
      columns={columns}
      description="Review service approval requests in your authorized tenant scope."
      fetchPage={fetchPage}
      filters={FILTERS}
      getActions={getActions}
      getRowId={(approval) => approval.approval_id}
      performAction={performAction}
      title="Approvals"
    />
  );
};

export default ApprovalsPage;
